<template>
	<div class="map-tooltip" :style="coords" ref="tooltipRoot">
		<div :class="mapTooltipStyle">
			<div v-if="fullMode && !isCollapsed" :class="headerClasses">
				<div class="flex-grow">{{ $t('home-map.tooltip.headers.' + mapMode) }}</div>
				<div v-if="!isCollapsed" class="collapse-icon">
					<DownCircleOutlined />
				</div>
			</div>
			<div v-else :class="headerClasses">
				<div class="flex-grow">{{ featureDataRef.mntpnt_int }}</div>
				<div v-if="!isCollapsed" class="collapse-icon">
					<DownCircleOutlined />
				</div>
			</div>
			<div v-if="!isCollapsed" class="map-tooltip-content">
				<MountpointDisplay v-if="fullMode" :baseStation="featureDataRef" />
				<div v-if="statsDisplay.length > 0">
					<div class="subtitle" v-if="fullMode && isMapModeGnss && isShownSatellites">{{ $t('home-map.tooltip.subtitles.satellites') }}</div>
					<div v-if="!fullMode || !isMapModeGnss || (isMapModeGnss && isShownSatellites)" class="col-chart-wrapper">
						<div
							:class="['col-chart-item', sizeSmallClass]"
							v-for="gnssStat in statsDisplay"
							v-memo="[featureDataRef.mntpnt_int, gnssStat.gnss, gnssStat.valueBright, gnssStat.valuePale, fullMode, allSubnetworksMode, selectedSubnetworkId, isCollapsed]"
							:key="gnssStat.gnss"
						>
							<div class="col-chart-item-bar" :style="gnssStat.columnBrightStyle">
								<div v-if="gnssStat.columnPaleVisible" class="col-chart-item-pale-bar" :style="gnssStat.columnPaleStyle" />
							</div>
							<div class="col-chart-item-value-bright" :style="gnssStat.itemValueStyle">{{ gnssStat.valueBright }}</div>
							<div v-if="fullMode && gnssStat.columnPaleVisible" class="col-chart-item-value-pale">
								{{ gnssStat.valuePale }}
							</div>
						</div>
					</div>
					<div v-if="!fullMode || !isMapModeGnss || (isMapModeGnss && isShownSatellites)" class="col-chart-caption-wrapper">
						<div :class="fullMode ? 'col-chart-item-caption' : 'col-chart-item-caption-small'" v-for="gnssStat in statsDisplay" :key="gnssStat.gnss">
							<div class="col-chart-item-caption-gnss">{{ fullMode ? gnssStat.gnssName : gnssStat.gnss }}</div>
						</div>
					</div>
					<div v-if="additionalUpdateItems.length > 0">
						<GnssTooltipUpdatedDisplay
							v-for="updateItem in additionalUpdateItems"
							:key="updateItem.label"
							:label="updateItem.label"
							:currentDatetime="updateItem.currentDatetime"
							:valueIsOld="updateItem.valueIsOld"
						/>
					</div>
					<div v-if="fullMode && isMapModeGnss && isShownSignals">
						<div class="subtitle">{{ $t('home-map.tooltip.subtitles.signals') }}</div>
						<GnssTooltipMountpointSignals v-for="gnssStat in statsDisplay"
							v-memo="[gnssStat.signals, gnssStat.currentDatetimeBright]" :key="gnssStat.gnss"
							:gnssStat="gnssStat" />
					</div>
					<div v-if="fullMode && isMapModeGnss && isShownRtcmMessages">
						<div v-if="rtcmMessages.length > 0">
							<div class="subtitle">{{ $t('home-map.tooltip.subtitles.rtcm-messages') }}</div>
							<div class="rtcm-messages-list">{{ rtcmMessages }}</div>
						</div>
						<div v-else>
							<div class="subtitle">{{ $t('home-map.tooltip.subtitles.rtcm-messages-no-data') }}</div>
						</div>
					</div>
				</div>
				<div v-else class="col-chart-no-data">
					{{ $t('home-map.tooltip.labels.noData') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { toRef, computed, onMounted, ref, inject } from 'vue';
import type { PropType } from 'vue';
import { useEventListener } from '@vueuse/core';
import { DownCircleOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n';

import Consts from '@/api/Consts';
import MapHelpers from '@/components/map/utils/MapHelpers';

import MountpointDisplay from '@/components/baseStations/MountpointDisplay.vue';

import GnssTooltipUpdatedDisplay from '@/components/map/tooltips/GnssTooltipUpdatedDisplay.vue';
import GnssTooltipMountpointSignals from '@/components/map/tooltips/GnssTooltipMountpointSignals.vue';

import { useProvideInjectZoom } from '@/utils/useProvideInject';
import { useLocaleData } from '@/locales/useLocaleData';
import { useSessionStore } from '@/stores/session';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import type { GnssTooltipFeatureData } from '@/stores/types/mapStoreContracts';
import type { MetricDbItem } from '@/stores/types/mapStoreContracts';


type TooltipCoords = {
	left: string;
	top: string;
};

export default {
	components: {
		MountpointDisplay, GnssTooltipMountpointSignals, DownCircleOutlined, GnssTooltipUpdatedDisplay,
	},
	props: {
		featureData: {
			type: Object as PropType<GnssTooltipFeatureData>,
			required: true,
		},
		coords: {
			type: Object as PropType<TooltipCoords>,
			required: true,
			validator: (value: TooltipCoords) => typeof value.left === 'string' && typeof value.top === 'string',
		},
		stats: {
			type: Object as PropType<MetricDbItem | null>,
			required: false,
			default: null,
		},
		hovered: {
			type: Boolean,
			required: true,
		},
		mapMode: {
			type: String,
			required: true,
		},
		focused: {
			type: Boolean,
			required: true,
		},
		allSubnetworksMode: {
			type: Boolean,
			required: true,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const isCollapsed = ref(false);
		const zoom = useProvideInjectZoom.injectValue();
		const fullMode = computed(() => zoom.value >= 9);
		const featureDataRef = toRef(props, 'featureData');
		const statsRef = toRef(props, 'stats');
		const hoveredRef = toRef(props, 'hovered');
		const focusedRef = toRef(props, 'focused');
		const allSubnetworksModeRef = toRef(props, 'allSubnetworksMode');
		const statsDisplay = computed(() => MapHelpers.getGnssStatsDisplay(
			statsRef,
			fullMode,
			allSubnetworksModeRef,
		));
		const rtcmMessages = computed((): string => {
			const firstStat = statsDisplay.value[0];
			if (firstStat == null) {
				return '';
			}
			const messages = firstStat.rtcmMessages;
			if (messages != null && messages.length > 0) {
				return messages;
			}
			return '';
		});
		const sizeSmallClass = computed(() => fullMode.value ? '' : 'col-small');
		const { localeData } = useLocaleData();
		const mapTooltipStyle = computed(() => {
			let styles = [
				'map-tooltip-style',
				'map-tooltip-position-' + localeData.value.direction,
			];
			if (hoveredRef.value) {
				styles.push('map-tooltip-style-hovered');
			} else if (focusedRef.value) {
				styles.push('map-tooltip-style-focused');
			} else if (isCollapsed.value) {
				styles.push('map-tooltip-style-collapsed');
			}
			return styles;
		});

		const tooltipRoot = ref<HTMLElement | undefined>();
		const unhoverMapFeatures = inject<(() => void) | null>('unhoverMapFeatures', null);
		const eventListener = (updateCollapsed: boolean) => (event: Event): void => {
			// если !updateCollapsed событие все равно перехватываем, чтобы не было неожиданных взаимодействий с картой
			if (!(event.target instanceof Element) || event.target.closest('.map-tooltip-header') == null) {
				return;
			}
			if (updateCollapsed) {
				isCollapsed.value = !isCollapsed.value;
			}
			event.stopPropagation();
			if (event.type === 'pointermove' && unhoverMapFeatures != null) {
				unhoverMapFeatures();
			}
		};
		onMounted(() => {
			useEventListener(tooltipRoot.value, 'pointerdown', eventListener(false));
			useEventListener(tooltipRoot.value, 'pointermove', eventListener(false));
			useEventListener(tooltipRoot.value, 'click', eventListener(true));
		});
		const headerClasses = computed(() => ({
			'flex-wrapper': true,
			'gap-sm': true,
			'map-tooltip-header': true,
			'map-tooltip-header-collapsed': isCollapsed.value,
		}));

		const sessionStore = useSessionStore();
		const mapModeRef = toRef(props, 'mapMode');
		const isShownItemType = (itemType: string): boolean => (
			sessionStore.mapModeGnssBSTooltipItems.includes(itemType)
		);
		// lookup: const MAP_MODES = [
		const isMapModeGnss = computed(() => mapModeRef.value === 'gnss');
		// показываем спутники для gnss и gridpoints в full mode
		const isShownSatellites = computed(() => isShownItemType('satellites'));
		// показываем сигналы для gnss и gridpoints в full mode
		const isShownSignals = computed(() => isShownItemType('signals'));
		// показываем сообщения RTCM для gnss и gridpoints в full mode
		const isShownRtcmMessages = computed(() => isShownItemType('rtcm-messages'));
		// показываем даты и время для всех режимов, кроме gnss
		const isShownDatetimesSeparately = computed(() => mapModeRef.value !== 'gnss');
		// надо ли данные показывать для разных GNSS
		const isShownDatetimesSplit = computed(() => ['snr', 'multipath'].includes(mapModeRef.value));
		// в режиме full mode показываем время для дополнительных элементов
		const additionalUpdateItems = computed(() => {
			const firstStat = statsDisplay.value[0];
			if (!fullMode.value || firstStat == null) {
				return [];
			}
			if (['snr', 'multipath'].includes(mapModeRef.value)) {
				// здесь выводим время для каждого GNSS
				return statsDisplay.value.map(stat => ({
					label: stat.gnssName,
					currentDatetime: stat.currentDatetimeBright,
					valueIsOld: stat.valueIsOld ?? false,
				}));
			}
			if (isMapModeGnss.value) {
				if (isShownSatellites.value && !allSubnetworksModeRef.value) {
					// здесь выводим общее время для спутников в решении с подписью
					return [{
						label: t('home-map.tooltip.labels.gnssInSolution'),
						currentDatetime: firstStat.currentDatetimePale,
						valueIsOld: firstStat.valueIsOld ?? false,
					}];
				}
				// не выводим, потому что в легенде пользователь скрыл гистограмму спутников
				return [];
			}
			// здесь выводим общее время для остальных режимов, без подписи
			return [{
				label: '',
				currentDatetime: firstStat.currentDatetimeBright,
				valueIsOld: firstStat.valueIsOld ?? false,
			}];
		});
		const subNetworksStore = useSubNetworksStore();
		const selectedSubnetworkId = computed(() => subNetworksStore.subNetworkSelectedId);
		return {
			statsDisplay,
			rtcmMessages,
			featureDataRef,
			fullMode,
			sizeSmallClass,
			mapTooltipStyle,
			tooltipRoot,
			isCollapsed,
			headerClasses,
			isMapModeGnss,
			isShownSatellites,
			isShownSignals,
			isShownRtcmMessages,
			isShownDatetimesSeparately,
			isShownDatetimesSplit,
			additionalUpdateItems,
			selectedSubnetworkId,
			dark0: Consts.COLORS.dark0,
		};
	},
}
</script>

<style scoped>
@import '@/assets/map-tooltip.css';

.col-chart-wrapper {
	display: flex;
	margin: 12px auto 0;
	gap: 4px;
	align-items: flex-end;
	max-width: 150px;
}

.col-chart-caption-wrapper {
	display: flex;
	gap: 4px;
	margin: 0 auto;
	max-width: 150px;
}

.col-chart-item,
.col-chart-item-caption,
.col-chart-item-caption-small {
	position: relative;
	flex-grow: 1;
	text-align: center;
}

.col-chart-item,
.col-chart-item-caption {
	width: 35px;
}

.col-small,
.col-chart-item-caption-small {
	width: 10px !important;
}

.col-chart-item-bar {
	border-radius: 0;
	border-start-start-radius: 3px;
	border-start-end-radius: 3px;
	position: relative;
}

.col-chart-item-pale-bar {
	border-radius: 3px 0 0 0;
	position: absolute;
	inset-block-end: 0;
	inset-inline-start: 5px;
	inset-inline-end: 0;
}

.col-chart-item-value-bright,
.col-chart-item-value-pale {
	position: absolute;
	font-weight: 600;
}

.col-chart-item-value-pale {
	inset-inline-start: 0;
	inset-inline-end: 0;
	inset-block-end: 0;
	font-size: 11px;
}

.col-chart-item-caption-gnss {
	font-size: 9px;
	color: white;
	background-color: v-bind(dark0);
}

.col-chart-no-data {
	min-width: 30px;
	margin-top: 0;
	margin-bottom: 5px;
	color: rgba(0, 0, 0, 0.25);
	text-align: center;
}

.collapse-icon {
	opacity: 0.7;
}

.map-tooltip-header:hover .collapse-icon {
	opacity: 1;
}

.subtitle {
	font-size: 14px;
	margin-top: 12px;
	font-weight: 600;
	line-height: 1.2;
}

.rtcm-messages-list {
	font-size: 12px;
}
</style>